import { DecryptedPatient, HealthcareParty } from '@icure/cardinal-sdk'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { Checkbox, CheckboxProps, Dropdown, MenuProps, message, notification, Tag, Tooltip } from 'antd'
import React, { ReactElement, useEffect, useState } from 'react'

import { deleteIcn, docsIcn, downloadIcn, gearIcn, keypairIcn, lockIcn, manageUserIcn, personAddIcn, shareIcn, stethoscopeIcn, uploadIcn, userIcn } from '../../../../assets/CustomIcons'
import { useEnsureUserForPatientMutation } from '../../../../core/api/userApi'
import { DropdownRow } from '../../../common/DropdownRow'
import { IconButton } from '../../../common/IconButton'
import { getPatientDataFormated } from '../../../../helpers/patientDataManipulations'
import { getTagColor } from '../../../../helpers/getTagColor'
import './index.less'
import { MedicalReportPDF } from '../MedicalReportPDF'
import { usePDF } from '@react-pdf/renderer'
import { SpinLoader } from '../../../common/SpinLoader'
import { useFindFormsByHcPartyPatientQuery } from '../../../../core/api/formApi'
import { useGeneratePdfReportQuery } from '../../../../core/api/pdfReportApi'
import { translateSection } from '../../../../helpers/translate'
import { ModalUploadReport } from '../../modals/ModalUploadReport'
import { BeCardinalProvider } from '@icure/be-hubs-access'
import { useAppDispatch, useAppSelector } from '../../../../core/hooks'
import { CardinalApiState, setEhealthPassphrase } from '../../../../core/services/auth.api'
import { createSelector } from '@reduxjs/toolkit'

interface UserRowProps {
  healthcareParty: HealthcareParty | undefined
  patient: DecryptedPatient
  onSelect: (checked: boolean) => void
  onAction: (action: string) => void
  isSelected?: boolean
  hasUserAccount?: boolean
}

const reduxSelector = createSelector(
  (state: { cardinalApi: CardinalApiState }) => state.cardinalApi,
  (cardinalApi: CardinalApiState) => ({
    ehealthPassphrase: cardinalApi.ehealthPassphrase,
  }),
)

export const UserRow = ({ healthcareParty, patient, onSelect, isSelected, onAction, hasUserAccount = false }: UserRowProps): ReactElement => {
  const [isOverFlowMenuOpen, setOverFlowMenuOpen] = useState(false)
  const [pdfAction, setPdfAction] = useState<'view' | 'download' | undefined>(undefined)
  const [generateReport, setGenerateReport] = useState(false)
  const [isUploadReportModalOpen, setUploadReportModalOpen] = useState(false)

  const { userNameOneString, emailAddress, tags, userHomeAddressOneString, birthSex, userDateOfBirthOneString } = getPatientDataFormated(patient)

  const dispatch = useAppDispatch()
  const { ehealthPassphrase } = useAppSelector(reduxSelector)
  const healthcarePartyId = healthcareParty?.id
  const parentHealthcarePartyId = healthcareParty?.parentId

  const { data: listOfForms } = useFindFormsByHcPartyPatientQuery(
    {
      hcPartyId: parentHealthcarePartyId ?? healthcarePartyId ?? '',
      patient,
    },
    { skip: !generateReport || (!parentHealthcarePartyId && !healthcarePartyId) || !patient },
  )
  const { data: pdfData } = useGeneratePdfReportQuery(
    { language: patient?.languages?.[0], patient, formId: listOfForms?.[0]?.id },
    { skip: !generateReport || !patient || !listOfForms },
  )
  const patientData = pdfData?.patientData
  const medicalOverview = pdfData?.medicalOverview
  const initialEvaluationData = pdfData?.initialEvaluationData
  const wellBeingSummary = pdfData?.wellBeingSummary

  const hasEmail = !!emailAddress && emailAddress !== '-'
  const canCreateUser = hasEmail && !hasUserAccount

  const [
    ensureUserForPatient,
    { error: invitePatientError, isError: isInvitePatientError, isSuccess: isPatientInvitedSuccessfully, isLoading: isPatientInvitingLoading, data: ensureUserResult },
  ] = useEnsureUserForPatientMutation()

  const [api, notificationContextHolder] = notification.useNotification()
  const [messageApi, messageContextHolder] = message.useMessage()

  const openNotification = (type: 'error', message: string, description: string) => {
    api.open({
      type,
      message,
      description,
      duration: 0,
    })
    setTimeout(api.destroy, 2500)
  }
  const showMessageFeedback = (type: 'loading' | 'success' | 'error', content: string) => {
    messageApi.open({
      type,
      content,
      duration: 0,
    })
    // Dismiss manually and asynchronously
    setTimeout(messageApi.destroy, type === 'loading' ? 100 : 2500)
  }

  useEffect(() => {
    if (isPatientInvitingLoading) showMessageFeedback('loading', 'Creating user account...')
    if (isPatientInvitedSuccessfully) {
      showMessageFeedback('success', ensureUserResult?.created ? 'User account created — invitation email sent.' : 'This patient already has a user account.')
    }
    if (isInvitePatientError)
      openNotification(
        'error',
        'Cannot create the user account!',
        `An error occurred while creating the user account. ${(invitePatientError as FetchBaseQueryError)?.data ?? invitePatientError}`,
      )
  }, [isPatientInvitingLoading, isPatientInvitedSuccessfully, isInvitePatientError, ensureUserResult?.created])

  const [pdfInstance, updatePdfInstance] = usePDF()

  const createPdf = () => {
    setGenerateReport(true)
  }

  useEffect(() => {
    if (patientData && medicalOverview && initialEvaluationData && wellBeingSummary) {
      translateSection(patient?.languages?.[0] ?? 'fr', 'medicalReportPDF').then((translations) => {
        const medicalReportPDF = (
          <MedicalReportPDF
            translations={translations}
            patientData={patientData}
            medicalOverview={medicalOverview}
            initialEvaluationData={initialEvaluationData}
            wellBeingSummary={wellBeingSummary}
          />
        )
        updatePdfInstance(medicalReportPDF)
      })
    }
  }, [patientData, medicalOverview, initialEvaluationData, wellBeingSummary])

  useEffect(() => {
    if (pdfAction === 'view' && pdfInstance.url) {
      window.open(pdfInstance.url, '_blank')
      setPdfAction(undefined)
    }
    if (pdfAction === 'download' && pdfInstance.url) {
      const link = document.createElement('a')
      link.href = pdfInstance.url
      link.download = `Rapport médical ${userNameOneString}.pdf` // Specify the default filename
      link.click() // Programmatically click the link to trigger the download
      setPdfAction(undefined)
    }
  }, [pdfInstance.url, pdfAction])

  type MenuItem = NonNullable<MenuProps['items']>[number]
  const createUserItems: MenuItem[] = canCreateUser
    ? [
        {
          key: 'create_user',
          label: <DropdownRow icon={personAddIcn} label="Create user account" />,
        },
        { type: 'divider' },
      ]
    : []

  const items: MenuProps['items'] = [
    ...createUserItems,
    {
      key: 'add_consultation',
      label: <DropdownRow icon={stethoscopeIcn} label="Add consultation" />,
    },
    {
      key: 'view_profile',
      label: <DropdownRow icon={userIcn} label="View profile" />,
    },
    {
      type: 'divider',
    },
    {
      key: 'view_report',
      label: <DropdownRow icon={docsIcn} label="View report" />,
    },
    {
      key: 'download_report',
      label: <DropdownRow icon={downloadIcn} label="Download report" />,
    },
    {
      key: 'upload_report',
      label: <DropdownRow icon={uploadIcn} label="Upload&nbsp;report&nbsp;on&nbsp;hubs" />,
    },
    {
      type: 'divider',
    },
    {
      key: 'edit_patient',
      label: <DropdownRow icon={manageUserIcn} label="Edit patient" />,
    },
    {
      key: 'share_patient',
      label: <DropdownRow icon={shareIcn} label="Share patient" />,
    },
    {
      key: 'delete_patient',
      danger: true,
      label: <DropdownRow icon={deleteIcn} label="Delete patient" />,
    },
  ]
  const onClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'view_report': {
        setPdfAction('view')
        createPdf()
        break
      }
      case 'download_report': {
        setPdfAction('download')
        createPdf()
        break
      }
      case 'upload_report': {
        setUploadReportModalOpen(true)
        break
      }
      case 'create_user': {
        if (hasEmail && emailAddress && userNameOneString) {
          ensureUserForPatient({
            email: emailAddress,
            patientId: patient.id,
            name: userNameOneString,
          })
        }
        break
      }
      default: {
        onAction(key)
      }
    }
  }
  const onCheck: CheckboxProps['onChange'] = (e) => {
    onSelect(e.target.checked)
  }
  const fhcUrl = healthcareParty?.properties.find((p) => p.id === 'fhcUrl')?.typedValue?.stringValue ?? 'https://fhcprd.icure.cloud'
  const hubId = healthcareParty?.properties.find((p) => p.id === 'hubId')?.typedValue?.stringValue ?? 'rsw'

  return (
    <>
      {notificationContextHolder}
      {messageContextHolder}
      {pdfInstance.loading && <SpinLoader />}
      {isUploadReportModalOpen && (
        <BeCardinalProvider
          onPassphraseVerified={(p) => {
            dispatch(setEhealthPassphrase({ ehealthPassphrase: p }))
          }}
          hubId={hubId ?? 'rsw'}
          hcpSsin={healthcareParty?.ssin ?? ''}
          fhcUrl={fhcUrl ?? 'https://fhcprd.icure.cloud'}
          environment={fhcUrl.includes('fhcacc') ? 'acc' : 'prod'}
          passphrase={ehealthPassphrase?.length ? ehealthPassphrase : undefined}
        >
          <ModalUploadReport isVisible={isUploadReportModalOpen} onClose={() => setUploadReportModalOpen(false)} patient={patient} />
        </BeCardinalProvider>
      )}
      <div className={`userRow ${isSelected ? 'isSelected' : ''}`} onClick={() => onAction('view_profile')}>
        <div className="userRow__checkboxContainer" onClick={(event) => event.stopPropagation()}>
          <Checkbox onChange={onCheck} checked={isSelected} />
        </div>

        <div className="userRow__contentDesktop">
          <div className="userRow__contentDesktop__item userRow__contentDesktop__item--name">
            <span className="userRow__contentDesktop__item__title">Name:</span>
            <span className="userRow__contentDesktop__item__value userRow__contentDesktop__item__value--name">{userNameOneString}</span>
          </div>
          <div className="userRow__contentDesktop__item userRow__contentDesktop__item--birthSex">
            <span className="userRow__contentDesktop__item__title">Sex at birth:</span>
            <span className="userRow__contentDesktop__item__value ">{birthSex ?? '-'}</span>
          </div>
          <div className="userRow__contentDesktop__item userRow__contentDesktop__item--language">
            <span className="userRow__contentDesktop__item__title">Date of birth:</span>
            <span className="userRow__contentDesktop__item__value ">{userDateOfBirthOneString}</span>
          </div>
          <div className="userRow__contentDesktop__item userRow__contentDesktop__item--email">
            <span className="userRow__contentDesktop__item__title">E-mail address:</span>
            <span className="userRow__contentDesktop__item__value">{emailAddress}</span>
          </div>
          <div className="userRow__contentDesktop__item userRow__contentDesktop__item--address">
            <span className="userRow__contentDesktop__item__title">Home address:</span>
            <span className="userRow__contentDesktop__item__value">{userHomeAddressOneString}</span>
          </div>
          <div className="userRow__contentDesktop__item">
            <span className="userRow__contentDesktop__item__title">Tags:</span>
            <div className="userRow__contentDesktop__item__tagsContainer">
              {tags?.length
                ? tags.map((tag, index) => (
                    <Tag key={index} color={getTagColor(tag.code)} bordered={false}>
                      {tag.code}
                    </Tag>
                  ))
                : '-'}
            </div>
          </div>
        </div>
        <div className="userRow__contentMobile">
          <span className="userRow__contentMobile__name">{userNameOneString}</span>
          <span className="userRow__contentMobile__value">
            {userDateOfBirthOneString}, {emailAddress}, {userHomeAddressOneString}
          </span>
          {tags?.length !== 0 && (
            <div className="userRow__contentMobile__tagsGroup">
              {tags?.map((tag, index) => (
                <Tag key={index} color={getTagColor(tag.code)} bordered={false}>
                  {tag.code}
                </Tag>
              ))}
            </div>
          )}
        </div>
        <div className="userRow__btnGroup" onClick={(e) => e.stopPropagation()}>
          {hasUserAccount && (
            <Tooltip title="This patient has a user account">
              <div>
                <IconButton icon={keypairIcn} />
              </div>
            </Tooltip>
          )}
          {patient.isEncrypted && (
            <Tooltip title="This patient could not be decrypted">
              <div>
                <IconButton icon={lockIcn} />
              </div>
            </Tooltip>
          )}
          <Tooltip title="Add consultation">
            <div>
              <IconButton className="userRow__btnGroup__item--addConsultation" onClick={() => onAction('add_consultation')} icon={stethoscopeIcn} />
            </div>
          </Tooltip>
          <Dropdown menu={{ items, onClick }} placement="bottomRight" arrow onOpenChange={(open: boolean) => setOverFlowMenuOpen(open)}>
            <div>
              <IconButton isActive={isOverFlowMenuOpen} icon={gearIcn} />
            </div>
          </Dropdown>
        </div>
      </div>
    </>
  )
}
